import { useEffect, useMemo, type ReactNode } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useAuth } from '../auth/AuthContext';
import type { AppConfig } from '../config';
import { DEMO_DATA_CHANGED, createBrowserApiClient } from './browserClient';
import { ApiContext, createHttpApiClient } from './client';

/** API client and query cache, both scoped to the signed-in user. */
export function ApiProvider({ config, children }: { config: AppConfig; children: ReactNode }) {
  const { user, getAuthHeaders } = useAuth();
  const api = useMemo(
    () =>
      config.dataMode === 'browser'
        ? createBrowserApiClient(getAuthHeaders, {
            liveSteps: config.liveSteps,
            envProfile: config.envProfile,
          })
        : createHttpApiClient(config.apiBaseUrl, getAuthHeaders),
    [config.dataMode, config.apiBaseUrl, config.liveSteps, config.envProfile, getAuthHeaders],
  );
  // A new cache per user, so switching persona never shows the previous user's data.
  const queryClient = useMemo(
    () => new QueryClient({ defaultOptions: { queries: { retry: 1 } } }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [user.upn],
  );
  // Another tab changed the demo data: refetch so this tab shows (and keeps) its changes.
  useEffect(() => {
    if (config.dataMode !== 'browser') return;
    const refresh = (): void => void queryClient.invalidateQueries();
    window.addEventListener(DEMO_DATA_CHANGED, refresh);
    return () => window.removeEventListener(DEMO_DATA_CHANGED, refresh);
  }, [config.dataMode, queryClient]);
  return (
    <ApiContext.Provider value={api}>
      <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>
    </ApiContext.Provider>
  );
}
